import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { useMemo, use, useState, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { LiteraryFeaturesByPassageDataContext } from '@/data/literary-features/literary-features-by-passage-data-context';
import { LiteraryFeaturesDataContext } from '@/data/literary-features/literary-feature-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import {
  searchLiteraryDevices,
  searchThemes,
  searchSubgenre,
  searchGenre,
  searchPoeticFeatures,
  searchImagery,
  searchNarrativeTechniques,
  searchMicroStructures,
  searchMacroStructures,
  searchForm,
  groupResultsByBook,
  type CrossReferenceResult
} from '@/lib/services/literary-feature-search-service';
import { BIBLE_BOOKS } from '@/lib/utils/constants-shared';
import { BIBLE_BOOKS_MAP, BIBLE_BOOKS_SORT_ORDER, type BibleBook } from '@/lib/utils/bible-utils';
import { LITERARY_TERM_DEFINITIONS } from '@/lib/constants/literary-terms';
import { BookOpen, BarChart3, Loader2, Sparkles, TrendingUp, Filter, History, ChevronRight } from 'lucide-react';
import { useHydrationStage } from '@/lib/hooks/use-hydration-stage';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useIsMobile } from '@/lib/hooks/use-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getSectionBackgroundColor } from '@/lib/utils/scripture-section-colors';

type FeatureType =
  | 'literaryDevice'
  | 'rhetoricalFeature'
  | 'theme'
  | 'subgenre'
  | 'genre'
  | 'poeticFeature'
  | 'imagery'
  | 'narrativeTechnique'
  | 'microStructure'
  | 'bookRhetoricalDevice'
  | 'macroStructure'
  | 'form';

type TestamentFilter = 'all' | 'ot' | 'nt';

interface LiteraryFeatureCrossReferenceModalProps {
  featureType: FeatureType;
  featureValue: string;
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to determine if a book is OT or NT
function getTestament(bookName: string): 'ot' | 'nt' {
  const bookInfo = BIBLE_BOOKS_MAP.get(bookName as BibleBook);
  // Matthew starts at sortOrder 39, so >= 39 is NT
  return bookInfo && bookInfo.sortOrder >= 39 ? 'nt' : 'ot';
}

function ResultItem({ result, index, onClose }: { result: CrossReferenceResult; index: number; onClose: () => void }) {
  const [, setUrl] = useUrl();
  const isDarkMode = useDarkMode();
  const section = (BIBLE_BOOKS_MAP.get(result.book as BibleBook))?.section;
  const sectionColor = section ? getSectionBackgroundColor(section, isDarkMode, true) : 'hsl(var(--primary))';

  const handleClick = () => {
    setUrl(`/app/passage/${result.passageId}/context/literary`);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className={cn(
        "group relative text-sm p-4 rounded-xl border cursor-pointer transition-all duration-300",
        isDarkMode
          ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
          : "bg-white border-black/5 hover:border-primary/30 hover:shadow-md"
      )}
      onClick={handleClick}
    >
      <div
        className="absolute left-0 top-4 bottom-4 w-1 rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
        style={{ backgroundColor: sectionColor }}
      />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h5 className={cn(
            "font-semibold transition-colors",
            isDarkMode ? "text-white/90 group-hover:text-white" : "text-gray-900 group-hover:text-primary"
          )}>
            {result.title}
          </h5>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={cn(
              "text-[10px] font-mono font-medium px-1.5 py-0.5 rounded",
              isDarkMode ? "bg-white/10 text-white/50" : "bg-gray-100 text-gray-500"
            )}>
              {result.ref}
            </span>
            <ChevronRight className="h-3 w-3 text-muted-foreground/30 group-hover:text-primary/50 transition-colors" />
          </div>
        </div>
        {result.context && (
          <p className={cn(
            "text-xs leading-relaxed line-clamp-2",
            isDarkMode ? "text-white/40 group-hover:text-white/60" : "text-gray-500 group-hover:text-gray-600"
          )}>
            {result.context}
          </p>
        )}
      </div>
    </motion.div>
  );
}

export function LiteraryFeatureCrossReferenceModal({
  featureType,
  featureValue,
  isOpen,
  onClose
}: LiteraryFeatureCrossReferenceModalProps) {
  const literaryFeaturesByPassageContext = use(LiteraryFeaturesByPassageDataContext);
  const literaryFeaturesContext = use(LiteraryFeaturesDataContext);
  const passageContext = use(PassageDataContext);
  const { isFull } = useHydrationStage();
  const isMobile = useIsMobile();

  const allPassageFeatures = literaryFeaturesByPassageContext?.literaryFeaturesByPassage || [];
  const allBookFeatures = literaryFeaturesContext?.literaryFeaturesByBook || [];
  const allPassages = passageContext?.data?.items || [];

  // State for filters
  const [testamentFilter, setTestamentFilter] = useState<TestamentFilter>('all');
  const [showChart, setShowChart] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CrossReferenceResult[] | null>(null);

  // Reset results when modal opens or feature changes
  useEffect(() => {
    if (isOpen) {
      setSearchResults(null);
      setIsSearching(true);
    }
  }, [isOpen, featureValue]);

  // Set initial chart visibility based on device type once hydrated and modal opens
  useEffect(() => {
    if (isFull && isOpen) {
      setShowChart(!isMobile);
    }
  }, [isFull, isMobile, isOpen]);

  // Lazy search execution - only runs when modal is open and hydrated
  useEffect(() => {
    if (!isOpen || !isFull || !featureValue) return;

    setIsSearching(true);

    // Using a small timeout to ensure the opening animation isn't blocked by the heavy filtering logic
    const timer = setTimeout(() => {
      let results: CrossReferenceResult[] = [];
      const normalizedValue = featureValue.toLowerCase(); // Use featureValue here

      try {
        if (featureType === 'literaryDevice') { // Use featureType here
          results = searchLiteraryDevices(featureValue, allPassageFeatures, allPassages);
        } else if (featureType === 'theme') {
          results = searchThemes(featureValue, allPassages);
        } else if (featureType === 'subgenre') {
          results = searchSubgenre(featureValue, allPassages);
        } else if (featureType === 'genre') {
          results = searchGenre(featureValue, allPassages);
        } else if (featureType === 'form') {
          results = searchForm(featureValue, allPassages);
        } else if (featureType === 'poeticFeature') {
          results = searchPoeticFeatures(featureValue, allPassageFeatures, allPassages);
        } else if (featureType === 'imagery') {
          results = searchImagery(featureValue, allPassageFeatures, allPassages);
        } else if (featureType === 'narrativeTechnique') {
          results = searchNarrativeTechniques(featureValue, allPassageFeatures, allPassages);
        } else if (featureType === 'microStructure') {
          results = searchMicroStructures(featureValue, allPassageFeatures, allPassages);
        } else if (featureType === 'macroStructure') {
          results = searchMacroStructures(featureValue, allBookFeatures, allPassages);
        }
      } catch (error) {
        console.error('Search failed:', error);
      }

      setSearchResults(results);
      setIsSearching(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [isOpen, isFull, featureValue, featureType, allPassageFeatures, allPassages, allBookFeatures]); // Use featureValue and featureType in dependencies

  // Filter results by testament
  const filteredResults = useMemo(() => {
    if (!searchResults) return [];
    if (testamentFilter === 'all') return searchResults;
    return searchResults.filter(result => getTestament(result.book) === testamentFilter);
  }, [searchResults, testamentFilter]);

  // Group results by book
  const groupedResults = useMemo(() => {
    return groupResultsByBook(filteredResults);
  }, [filteredResults]);

  // Sort books by biblical order
  const sortedBooks = useMemo(() => {
    return Object.keys(groupedResults).sort((a, b) => {
      const indexA = BIBLE_BOOKS_SORT_ORDER[a] || 0;
      const indexB = BIBLE_BOOKS_SORT_ORDER[b] || 0;
      return indexA - indexB;
    });
  }, [groupedResults]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!searchResults) return {
      total: 0, totalBooks: 0, otCount: 0, ntCount: 0, otBooks: 0, ntBooks: 0, mostCommonBook: '', mostCommonCount: 0
    };

    const otCount = searchResults.filter(r => getTestament(r.book) === 'ot').length;
    const ntCount = searchResults.filter(r => getTestament(r.book) === 'nt').length;
    const otBooks = new Set(searchResults.filter(r => getTestament(r.book) === 'ot').map(r => r.book)).size;
    const ntBooks = new Set(searchResults.filter(r => getTestament(r.book) === 'nt').map(r => r.book)).size;

    // Find book with most occurrences
    const bookCounts = new Map<string, number>();
    searchResults.forEach(r => {
      bookCounts.set(r.book, (bookCounts.get(r.book) || 0) + 1);
    });
    let maxBook = '';
    let maxCount = 0;
    bookCounts.forEach((count, book) => {
      if (count > maxCount) {
        maxCount = count;
        maxBook = book;
      }
    });

    return {
      total: searchResults.length,
      totalBooks: new Set(searchResults.map(r => r.book)).size,
      otCount,
      ntCount,
      otBooks,
      ntBooks,
      mostCommonBook: maxBook,
      mostCommonCount: maxCount
    };
  }, [searchResults]);

  const featureTypeLabel = {
    literaryDevice: 'Literary Device',
    rhetoricalFeature: 'Rhetorical Feature',
    theme: 'Theme',
    subgenre: 'Subgenre',
    genre: 'Genre/Form',
    poeticFeature: 'Poetic Feature',
    imagery: 'Imagery',
    narrativeTechnique: 'Narrative Technique',
    microStructure: 'Micro Structure',
    bookRhetoricalDevice: 'Rhetorical Device (Book-level)',
    macroStructure: 'Macro Structure',
    form: 'Literary Form'
  }[featureType];

  const isDarkMode = useDarkMode();

  // Get definition if available
  const definition = LITERARY_TERM_DEFINITIONS[featureValue] ||
    LITERARY_TERM_DEFINITIONS[featureValue.split(' (')[0]] || // Handle "Term (Variant)"
    null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "max-w-4xl max-h-[90vh] p-0 overflow-hidden flex flex-col border shadow-2xl transition-all duration-300",
          isDarkMode
            ? "bg-neutral-900/60 border-white/10 text-white shadow-black/80"
            : "bg-white/80 border-black/5 text-neutral-900 shadow-black/10"
        )}
        style={{ backdropFilter: 'blur(16px)' }}
      >
        <DialogTitle className="sr-only">"{featureValue}" across Scripture</DialogTitle>
        <DialogDescription className="sr-only">
          List of all occurrences of "{featureValue}" in other parts of the Bible.
        </DialogDescription>

        {/* Scrollable Container for Mobile, Fixed Layout for Desktop */}
        <div className={cn(
          "flex-1 flex flex-col min-h-0",
          isMobile ? "overflow-y-auto" : "overflow-hidden"
        )}>
          {/* Cinematic Header Container */}
          <div className={cn(
            "relative pt-8 pb-10 px-8 border-b transition-colors shrink-0",
            isDarkMode
              ? "bg-white/[0.02] border-white/10"
              : "bg-black/[0.01] border-black/[0.05]"
          )}>
            {/* Decorative background element */}
            <div className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 opacity-[0.03] pointer-events-none">
              <BookOpen className="h-64 w-64 rotate-12" />
            </div>

            <div className="relative z-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "p-1.5 rounded-lg",
                    isDarkMode ? "bg-primary/20 text-primary-foreground" : "bg-primary/10 text-primary"
                  )}>
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <span className={cn(
                    "text-[10px] font-bold uppercase tracking-[0.2em]",
                    isDarkMode ? "text-primary/60" : "text-primary/70"
                  )}>
                    Scriptural Cross-Reference
                  </span>
                </div>
                <h2 className={cn(
                  "text-2xl md:text-3xl font-serif font-bold tracking-tight",
                  isDarkMode ? "text-white" : "text-gray-900"
                )}>
                  "{featureValue}"
                </h2>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] font-medium tracking-wide uppercase px-2 py-0.5",
                        isDarkMode ? "border-white/10 text-white/60" : "border-black/5 text-gray-500"
                      )}
                    >
                      {featureTypeLabel}
                    </Badge>
                  </div>
                  {definition && (
                    <p className={cn(
                      "text-sm max-w-xl leading-relaxed mt-1",
                      isDarkMode ? "text-white/70" : "text-gray-600"
                    )}>
                      {definition}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right hidden sm:block">
                  <div className="text-2xl font-bold font-serif">{stats.total}</div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-60">Total Passages</div>
                </div>
                <div className="h-10 w-px bg-border/20 hidden sm:block" />
                <div className="text-right hidden sm:block">
                  <div className="text-2xl font-bold font-serif">{stats.totalBooks}</div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-60">Across Books</div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className={cn(
            "p-6 space-y-6 flex flex-col",
            isMobile ? "flex-none" : "flex-1 overflow-hidden"
          )}>
            {/* Statistics & Chart Panel */}
            {stats.total > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "rounded-2xl p-4 transition-all duration-500",
                  isDarkMode ? "bg-white/[0.03] border border-white/5" : "bg-gray-50/50 border border-black/5"
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground opacity-70">
                    <TrendingUp className="h-3 w-3" />
                    Distribution Insights
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowChart(!showChart)}
                    className={cn(
                      "h-7 text-[10px] font-bold uppercase tracking-widest px-2.5",
                      isDarkMode ? "hover:bg-white/5" : "hover:bg-black/5"
                    )}
                  >
                    <BarChart3 className="h-3 w-3 mr-1.5" />
                    {showChart ? 'Hide' : 'Show'} Details
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-2">
                  <div className="space-y-0.5">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-50">Old Testament</div>
                    <div className="text-sm font-bold flex items-baseline gap-1.5">
                      {stats.otCount}
                      <span className="text-[10px] font-medium opacity-40">in {stats.otBooks} books</span>
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-50">New Testament</div>
                    <div className="text-sm font-bold flex items-baseline gap-1.5">
                      {stats.ntCount}
                      <span className="text-[10px] font-medium opacity-40">in {stats.ntBooks} books</span>
                    </div>
                  </div>
                  <div className="space-y-0.5 col-span-2">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground opacity-50">High Concentration</div>
                    <div className="text-sm font-bold flex items-baseline gap-1.5 truncate">
                      {stats.mostCommonBook}
                      <span className="text-[10px] font-medium opacity-40 whitespace-nowrap">({stats.mostCommonCount} occurrences)</span>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {showChart && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-2 border-t border-border/10">
                        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-2 custom-scrollbar">
                          {sortedBooks.map(book => {
                            const count = groupedResults[book].length;
                            const relativePercentage = (count / stats.mostCommonCount) * 100;
                            const section = (BIBLE_BOOKS_MAP.get(book as BibleBook))?.section;
                            const sectionColor = section ? getSectionBackgroundColor(section, isDarkMode, true) : 'hsl(var(--primary))';

                            return (
                              <div key={book} className="group flex items-center gap-3">
                                <div className="w-24 text-[10px] font-bold font-serif text-muted-foreground truncate group-hover:text-foreground transition-colors">{book}</div>
                                <div className="flex-1 bg-muted/30 rounded-full h-3.5 overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${relativePercentage}%` }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                    className="h-full flex items-center justify-end pr-1.5 transition-all opacity-70 group-hover:opacity-100"
                                    style={{ backgroundColor: sectionColor }}
                                  >
                                    {relativePercentage > 15 && (
                                      <span className="text-[8px] font-black text-white drop-shadow-sm px-0.5">
                                        {count}
                                      </span>
                                    )}
                                  </motion.div>
                                </div>
                                {relativePercentage <= 15 && (
                                  <span className="text-[9px] font-bold text-muted-foreground/40 w-4">
                                    {count}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Controls Hook */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full border",
                  isDarkMode ? "bg-white/[0.03] border-white/5" : "bg-gray-50 border-black/5"
                )}>
                  <Filter className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground opacity-60">Testament Filter</span>
                </div>
                <ButtonGroup className="p-1 rounded-xl bg-muted/30 border border-border/10">
                  {(['all', 'ot', 'nt'] as const).map((filter) => (
                    <Button
                      key={filter}
                      variant="ghost"
                      size="sm"
                      onClick={() => setTestamentFilter(filter)}
                      className={cn(
                        "h-8 px-4 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all",
                        testamentFilter === filter
                          ? (isDarkMode ? "bg-white/10 text-white shadow-sm" : "bg-white text-primary shadow-sm")
                          : "text-muted-foreground hover:bg-transparent opacity-50 hover:opacity-100"
                      )}
                    >
                      {filter === 'all' ? 'Scripture' : filter.toUpperCase()}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground opacity-40 hover:opacity-100 transition-opacity">
                <History className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-widest">
                  {filteredResults.length} found
                </span>
              </div>
            </div>

            {/* Results Area */}
            <div className={cn(
              "bg-muted/10 rounded-2xl border border-border/10 flex flex-col",
              isMobile ? "flex-none" : "flex-1 min-h-0 overflow-hidden"
            )}>
              {!isFull || isSearching || searchResults === null ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-center text-muted-foreground gap-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full scale-150 animate-pulse" />
                    <Loader2 className="h-10 w-10 animate-spin text-primary relative z-10" />
                  </div>
                  <div className="space-y-2">
                    <p className="font-serif text-lg font-medium text-foreground">Analyzing Scriptures</p>
                    <p className="text-xs max-w-[240px] leading-relaxed opacity-60">
                      We're preparing your cross-reference index. This may take a moment on the first visit...
                    </p>
                  </div>
                </div>
              ) : filteredResults.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-center text-muted-foreground gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-muted/30 flex items-center justify-center mb-1">
                    <BookOpen className="h-6 w-6 opacity-20" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-serif font-medium text-foreground">No matches found</p>
                    <p className="text-xs opacity-50">
                      {testamentFilter === 'all'
                        ? `No occurrences of ${featureValue} in other books.`
                        : `No occurrences found in the ${testamentFilter.toUpperCase()}.`
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <div className={cn(
                  "custom-scrollbar",
                  isMobile ? "overflow-visible" : "flex-1 overflow-y-auto"
                )}>
                  <div className="space-y-10 py-4 px-4">
                    {isSearching ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <Loader2 className="h-10 w-10 animate-spin text-primary/40" />
                        <p className="text-sm font-medium text-muted-foreground animate-pulse">
                          Scanning the macroscope for connections...
                        </p>
                      </div>
                    ) : sortedBooks.map(book => (
                      <div key={book} className="space-y-4">
                        <div className="sticky top-0 z-20 bg-transparent flex items-center gap-3">
                          <div className={cn(
                            "absolute inset-0 backdrop-blur-md -mx-4 -top-2 h-[calc(100%+16px)]",
                            isDarkMode
                              ? "bg-neutral-900/60 shadow-sm"
                              : "bg-white/70 border-b border-black/5 shadow-sm"
                          )} />
                          <div className="relative flex items-center gap-3 px-1 py-3">
                            <h3 className="font-serif text-lg font-bold">
                              {book}
                            </h3>
                            <Badge
                              variant="secondary"
                              className={cn(
                                "text-[10px] font-black h-5 min-w-5 flex items-center justify-center rounded-full",
                                isDarkMode ? "bg-white/10 text-white" : "bg-gray-100 text-gray-500"
                              )}
                            >
                              {groupedResults[book].length}
                            </Badge>
                          </div>
                        </div>
                        <div className="grid gap-3">
                          {groupedResults[book].map((result, idx) => (
                            <ResultItem key={result.passageId} result={result} index={idx} onClose={onClose} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

